/**
 * CelebrationScreen Component
 *
 * Post-onboarding celebration overlay shown after user completes all steps.
 * Displays an animated checkmark with congratulatory copy before
 * auto-navigating to the dashboard. Respects prefers-reduced-motion
 * by swapping the pop animation for a fade-in.
 */

import React, { useEffect, useState } from "react";
import { CircleCheckBig, Loader2 } from "lucide-react";
import { cn } from "../utils/cn";

/** Delay in ms before showing the auto-advance spinner */
const SPINNER_DELAY_MS = 1000;

/**
 * CelebrationScreen — Post-onboarding success moment
 *
 * Full-screen overlay with animated checkmark, headline, subtext,
 * and auto-advance spinner. Applies fade-in animation instead of
 * pop when the user prefers reduced motion.
 */
export const CelebrationScreen: React.FC = () => {
  const [showSpinner, setShowSpinner] = useState(false);
  const [showCelebrationImage, setShowCelebrationImage] = useState(true);
  const prefersReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpinner(true);
    }, SPINNER_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      className="celebration-screen"
      id="celebration-screen"
      role="status"
      aria-live="polite"
    >
      {showCelebrationImage ? (
        <img
          className={cn(
            "celebration-checkmark",
            prefersReducedMotion && "celebration-reduced-motion"
          )}
          src="/images/onboarding/celebration.png"
          alt=""
          aria-hidden="true"
          onError={() => setShowCelebrationImage(false)}
        />
      ) : (
        <CircleCheckBig
          className={cn(
            "celebration-checkmark",
            prefersReducedMotion && "celebration-reduced-motion"
          )}
          aria-hidden="true"
        />
      )}
      <h1 className="celebration-headline">You&apos;re all set!</h1>
      <p className="celebration-subtext">
        Let&apos;s see your retirement projections...
      </p>
      {showSpinner && (
        <Loader2 className="celebration-spinner" size={24} aria-hidden="true" />
      )}
    </div>
  );
};

export default CelebrationScreen;
